"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Microphone capture as a value-returning state machine.
 *
 * Pulled out of `ImpromptuExperience` because the MediaRecorder lifecycle was
 * interleaved with feature logic there, and the two failed together: a recorder
 * that died mid-attempt set the same error state as a rejected upload, so the
 * interface offered to "send it again" when there was nothing to send.
 *
 * Failures come back as values rather than thrown errors, matching
 * `PronunciationAudioResult` in `lib/impromptu/wav.ts`. The one exception is
 * genuinely asynchronous: a recorder can fail while nobody is awaiting it, which
 * is what `onFailure` is for.
 */

export type AudioRecorderFailureCode =
  /** No MediaRecorder or no getUserMedia. Retrying cannot help. */
  | "unsupported_browser"
  /** The learner declined, or the browser blocked the permission prompt. */
  | "microphone_blocked"
  /** We had a stream and lost it — device unplugged, tab stolen, OS interrupt. */
  | "microphone_lost"
  /** MediaRecorder refused to construct, usually over the requested MIME type. */
  | "recorder_unavailable"
  /** The recorder errored during or at the end of capture. */
  | "recording_failed";

export type AudioRecorderArmResult = { ok: true } | { ok: false; code: AudioRecorderFailureCode };

export type AudioRecorderStartResult = { ok: true } | { ok: false; code: AudioRecorderFailureCode };

export type AudioRecorderStopResult =
  | { ok: true; blob: Blob; durationMs: number }
  | { ok: false; code: AudioRecorderFailureCode };

/**
 * Ordered by preference, not by popularity. Opus in a WebM container is the only
 * combination every provider in this codebase accepts without transcoding;
 * `audio/mp4` is here because Safari offers nothing else.
 */
const CANDIDATE_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "audio/webm",
  "audio/ogg",
] as const;

function chooseAudioMimeType(): string | null {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return null;
  for (const candidate of CANDIDATE_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

/**
 * Providers routinely sniff the filename extension rather than the declared
 * content type, so an upload named `.webm` carrying MP4 bytes gets rejected as
 * corrupt. Derive the extension from what the recorder actually produced.
 */
export function extensionForAudioMimeType(mimeType: string): string {
  if (mimeType.includes("wav")) return "wav";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  return "webm";
}

export function isAudioRecordingSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
}

type UseAudioRecorderOptions = {
  audioBitsPerSecond?: number;
  /**
   * Called when capture fails without anyone waiting on it. Keep it stable
   * (`useCallback`) — it is read through a ref, so a changing identity will not
   * restart anything, but a stale closure would report to the wrong attempt.
   */
  onFailure?: (code: AudioRecorderFailureCode) => void;
};

export function useAudioRecorder({ onFailure, audioBitsPerSecond }: UseAudioRecorderOptions = {}) {
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const stoppingRef = useRef(false);
  const onFailureRef = useRef(onFailure);

  // The callback is read through a ref so `cancel` can stay referentially stable
  // and therefore safe to use as an unmount cleanup function.
  useEffect(() => {
    onFailureRef.current = onFailure;
  }, [onFailure]);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      // Detached before stopping: these handlers report to an attempt that is
      // being abandoned, and `onstop` in particular would resolve a promise
      // whose consumer has already moved on.
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch {
        // The stream is released below regardless of what state the recorder
        // decided it was in.
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = 0;
    stoppingRef.current = false;
    releaseStream();
  }, [releaseStream]);

  useEffect(() => cancel, [cancel]);

  /**
   * Opens the microphone without recording. Separate from `start` because the
   * permission prompt has to be answered before a preparation countdown can be
   * honest about when speaking begins.
   */
  const arm = useCallback(async (): Promise<AudioRecorderArmResult> => {
    if (!isAudioRecordingSupported()) return { ok: false, code: "unsupported_browser" };
    cancel();
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      return { ok: true };
    } catch {
      releaseStream();
      return { ok: false, code: "microphone_blocked" };
    }
  }, [cancel, releaseStream]);

  const start = useCallback((): AudioRecorderStartResult => {
    const stream = streamRef.current;
    if (!stream || typeof MediaRecorder === "undefined") {
      return { ok: false, code: "microphone_lost" };
    }
    const mimeType = chooseAudioMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        ...(audioBitsPerSecond ? { audioBitsPerSecond } : {}),
      });
    } catch {
      releaseStream();
      return { ok: false, code: "recorder_unavailable" };
    }

    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      recorderRef.current = null;
      releaseStream();
      onFailureRef.current?.("recording_failed");
    };
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    stoppingRef.current = false;
    // One-second timeslices so a crash mid-attempt still leaves usable audio
    // behind, rather than one buffer that is lost whole.
    recorder.start(1_000);
    return { ok: true };
  }, [releaseStream, audioBitsPerSecond]);

  /**
   * Stops capture and resolves the recording. Duration is wall-clock from
   * `start`, not derived from the blob: container metadata is unreliable across
   * browsers, and this number is what pace is calculated from.
   */
  const stop = useCallback(async (): Promise<AudioRecorderStopResult> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive" || stoppingRef.current) {
      return { ok: false, code: "recording_failed" };
    }
    stoppingRef.current = true;
    const durationMs = Date.now() - startedAtRef.current;

    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        recorder.onstop = () =>
          resolve(new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" }));
        recorder.onerror = () => reject(new Error("recording_failed"));
        recorder.stop();
      });
      return { ok: true, blob, durationMs };
    } catch {
      return { ok: false, code: "recording_failed" };
    } finally {
      recorderRef.current = null;
      chunksRef.current = [];
      stoppingRef.current = false;
      releaseStream();
    }
  }, [releaseStream]);

  /** Milliseconds since `start`, or 0 when not recording. */
  const elapsedMs = useCallback(() => {
    if (!startedAtRef.current) return 0;
    return Date.now() - startedAtRef.current;
  }, []);

  /** Read-only access for a local meter; capture lifecycle stays with this hook. */
  const getStream = useCallback(() => streamRef.current, []);

  // Memoised because callers put this object in effect dependency arrays. A fresh
  // object each render would restart the caller's countdown timers on every
  // unrelated re-render, which silently stretches a fixed preparation time.
  return useMemo(
    () => ({ arm, start, stop, cancel, elapsedMs, getStream }),
    [arm, start, stop, cancel, elapsedMs, getStream]
  );
}
